import {
  fromIso8601Date,
  fromIso8601Time,
  toIso8601Date,
  toIso8601Time,
} from "@/shared/lib/datetime";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseDatePicker } from "@/shared/ui/base-date-picker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseLink } from "@/shared/ui/base-link";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { BaseTimeField } from "@/shared/ui/base-time-field";
import { BaseToggle, type ToggleOptionType } from "@/shared/ui/base-toggle";
import { DeliveryRouteTaskContentTags } from "@/widgets/delivery-route-task-content-tags";
import { PrefectureField } from "@/widgets/prefecture-field";
import {
  AccordionDetails,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import {
  type Control,
  Controller,
  type UseFormWatch,
  useFieldArray,
} from "react-hook-form";
import type { CreateContractFormSchemaType } from "../../../model/validation";

const TASK_DETAILS_LIST = [
  { value: "forklift_use_owner", label: "フォークリフト(自主荷役あり)" },
  { value: "forklift_use_driver", label: "フォークリフト(自主荷役なし)" },
  { value: "handlift", label: "ハンドリフト" },
  { value: "unloading", label: "バラ" },
  { value: "crane", label: "クレーン" },
  { value: "other", label: "その他" },
] as const;

export const DestinationForm = ({
  index,
  taskContent,
  control,
  watch,
}: {
  index: number;
  taskContent: "loading" | "unloading" | null;
  control: Control<CreateContractFormSchemaType>;
  watch: UseFormWatch<CreateContractFormSchemaType>;
}) => {
  const TIME_OPTIONS: ToggleOptionType[] = [
    { value: "till", label: "までに" },
    { value: "after", label: "以降" },
    { value: "just", label: "ちょうど" },
    { value: "earliest", label: "最短" },
    { value: "term", label: "時間帯指定" },
  ];

  const checkedIsLoad = watch(`destinations.${index}.hasLoading`);
  const checkedIsUnload = watch(`destinations.${index}.hasUnloading`);
  const selectedDeadlineType = watch(`destinations.${index}.deadlineType`);

  const title =
    taskContent === "loading"
      ? "積地"
      : taskContent === "unloading"
        ? "卸地"
        : "経由地";

  return (
    <Stack
      spacing={3}
      bgcolor={"grey.50"}
      border="1px solid"
      borderColor={"grey.100"}
      width={"100%"}
      p={2}
    >
      <Stack direction="row" spacing={1} display="flex" alignItems="center">
        <Typography variant="body1" fontWeight={700}>
          【{title}】
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          作業内容：
        </Typography>
        <DeliveryRouteTaskContentTags
          hasLoading={!!checkedIsLoad}
          hasUnloading={!!checkedIsUnload}
        />
      </Stack>
      {taskContent === "loading" || taskContent === "unloading" ? (
        <BaseFormLabel label="日時" isRequired sx={{ width: "100%" }}>
          <Controller
            name={taskContent === "loading" ? "loadingOn" : "unloadingOn"}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <BaseDatePicker
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  value={field.value ? fromIso8601Date(field.value) : undefined}
                  onChange={(value) =>
                    field.onChange(value ? toIso8601Date(value) : null)
                  }
                />
              );
            }}
          />
        </BaseFormLabel>
      ) : null}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={3}>
        <BaseFormLabel label="時間指定" isRequired>
          <Controller
            name={`destinations.${index}.deadlineType`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseToggle
                {...field}
                options={TIME_OPTIONS}
                size="small"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel label="時間">
          <Box display="flex" gap="10px" alignItems="center">
            <Controller
              name={`destinations.${index}.deadlineTime`}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <BaseTimeField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    value={
                      field.value ? fromIso8601Time(field.value) : undefined
                    }
                    onChange={(value) =>
                      field.onChange(value ? toIso8601Time(value) : undefined)
                    }
                  />
                );
              }}
            />
            {selectedDeadlineType === "term" && (
              <>
                <Typography justifyItems="Center">–</Typography>
                <Controller
                  name={`destinations.${index}.deadlineTimeEnd`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <BaseTimeField
                        {...field}
                        // 積卸し日時は必須項目だが変更可能
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        value={
                          field.value ? fromIso8601Time(field.value) : undefined
                        }
                        onChange={(value) =>
                          field.onChange(
                            value ? toIso8601Time(value) : undefined,
                          )
                        }
                      />
                    );
                  }}
                />
              </>
            )}
          </Box>
        </BaseFormLabel>
      </Box>
      <Box display="flex" gap={3}>
        <BaseFormLabel label="郵便番号">
          <Controller
            name={`destinations.${index}.zipCode`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                size="small"
              />
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel label="都道府県" isRequired sx={{ maxWidth: "376px" }}>
          <Controller
            name={`destinations.${index}.prefecture`}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl size="small" fullWidth error={fieldState.invalid}>
                <PrefectureField
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  size="small"
                />
              </FormControl>
            )}
          />
        </BaseFormLabel>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="stretch" gap={3}>
        <BaseFormLabel
          label="住所"
          isRequired
          sx={{ maxWidth: { xs: "100%", sm: "376px" } }}
        >
          <Controller
            name={`destinations.${index}.address`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                size="small"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </BaseFormLabel>
      </Box>
      <Box display="flex" gap={3} sx={{ maxWidth: "376px" }}>
        <BaseFormLabel label="建物名" sx={{ width: "100%" }}>
          <Controller
            name={`destinations.${index}.buildingName`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                fullWidth
                size="small"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </BaseFormLabel>
      </Box>
      <Box display="flex" gap={3} maxWidth="376px">
        <BaseFormLabel label="担当者名">
          <Controller
            name={`destinations.${index}.chargePersonName`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                size="small"
              />
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel label="連絡先">
          <Controller
            name={`destinations.${index}.contactInfo`}
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                size="small"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </BaseFormLabel>
      </Box>
      <Box display="flex" gap={3} maxWidth="376px">
        <BaseFormLabel label="作業内容" sx={{ width: "100%" }}>
          <Controller
            name={`destinations.${index}.taskDetail`}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} size="small" fullWidth>
                <BaseSelect
                  {...field}
                  type="string"
                  sx={{ width: 256 }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  noSelectedOptionText="(選択しない)"
                >
                  {TASK_DETAILS_LIST.map((task) => (
                    <MenuItem key={task.value} value={task.value}>
                      {task.label}
                    </MenuItem>
                  ))}
                </BaseSelect>
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </BaseFormLabel>
      </Box>
      <Box display="flex" gap={3} maxWidth="376px">
        <Controller
          name={`destinations.${index}.memo`}
          control={control}
          render={({ field, fieldState }) => (
            <BaseFormLabel label="特記事項(日付など)" sx={{ width: "100%" }}>
              <BaseTextField
                {...field}
                fullWidth
                size="small"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            </BaseFormLabel>
          )}
        />
      </Box>
    </Stack>
  );
};

export const DestinationsForm = (props: {
  deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo;
  control: Control<CreateContractFormSchemaType>;
  watch: UseFormWatch<CreateContractFormSchemaType>;
}) => {
  const { fields } = useFieldArray({
    control: props.control,
    name: "destinations",
  });

  return (
    <BaseAccordion>
      <BaseAccordionSummary>配送スケジュール・ルート</BaseAccordionSummary>
      <AccordionDetails>
        <Stack gap={3} mb={2}>
          {fields.map((field, index) => (
            <DestinationForm
              key={field.id}
              {...props}
              index={index}
              taskContent={
                index === 0
                  ? "loading"
                  : index === fields.length - 1
                    ? "unloading"
                    : null
              }
            />
          ))}
          {props.deliveryProposalOfferCargo.attachedDestinationFiles && (
            <BaseFormLabel label="添付ファイル">
              <Stack>
                {props.deliveryProposalOfferCargo.attachedDestinationFiles.map(
                  (file) => (
                    <BaseLink key={file.url} href={file.url} variant="caption">
                      {file.filename}
                    </BaseLink>
                  ),
                )}
              </Stack>
            </BaseFormLabel>
          )}
        </Stack>
      </AccordionDetails>
    </BaseAccordion>
  );
};
